import React, { useState, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, ArrowRight, ShieldCheck, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";

const HERO_BG = "https://static.prod-images.emergentagent.com/jobs/aaa00cee-2ba1-4bc5-af9f-cc5c85924554/images/9c5c08fbd972a934d339fe1e1aecd25b48f5235f4ee1ccc97849c6ac4c412634.png";

export default function Login() {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [needsSetup, setNeedsSetup] = useState(null);

  useEffect(() => {
    api.get("/auth/setup-check")
      .then(({ data }) => setNeedsSetup(data.needs_setup))
      .catch(() => setNeedsSetup(false));
  }, []);

  if (user) return <Navigate to="/app/dashboard" replace />;
  if (needsSetup === true) return <Navigate to="/setup" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/app/dashboard");
    } catch (err) {
      setError(err?.response?.data?.detail || "Erreur de connexion");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-2 bg-obsidian-950" data-testid="login-page">
      {/* Left — hero */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden border-r border-line">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            filter: "brightness(0.4) saturate(1.1)",
          }}
        />
        <div className="absolute inset-0 z-0 bg-gradient-to-br from-obsidian-950/90 via-obsidian-950/60 to-obsidian-950/95" />

        <div className="relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-citron grid place-items-center font-mono text-lg font-bold text-obsidian-950">
              GR
            </div>
            <div>
              <div className="font-heading text-lg font-bold tracking-tight">GROUPE ROPS</div>
              <div className="overline">v2 — Espace Direction</div>
            </div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="relative z-10 max-w-md"
        >
          <div className="overline mb-6 text-citron">Direction · Gestion intégrée · 2026</div>
          <h1 className="font-heading text-5xl xl:text-6xl font-bold tracking-tighter leading-[1.05] mb-6 text-balance">
            Pilotez l'intégralité de votre <span className="text-citron">conglomérat</span>.
          </h1>
          <p className="text-obsidian-300 text-base leading-relaxed">
            Comptabilité OHADA, trésorerie, RH, achats, HSE — sur les 7 entités du Groupe. Augmenté par un copilote IA dédié à la direction.
          </p>

          <div className="mt-10 flex items-center gap-6 text-xs text-obsidian-400 font-mono">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-citron" /> JWT · bcrypt · audit</span>
            <span className="inline-flex items-center gap-1.5">⚡ Claude Sonnet 4.5</span>
          </div>
        </motion.div>

        <div className="relative z-10 overline">© 2026 — GROUPE ROPS · Libreville, Gabon</div>
      </div>

      {/* Right — form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-md"
        >
          <div className="mb-10">
            <div className="overline mb-3 text-citron">Connexion sécurisée</div>
            <h2 className="font-heading text-4xl font-bold tracking-tight">Bon retour.</h2>
            <p className="text-obsidian-400 text-sm mt-2">Accédez à votre espace de direction.</p>
          </div>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label block" htmlFor="email">Email</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                <input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="archil@grouperops.ga"
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="label block" htmlFor="password">Mot de passe</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-obsidian-500" />
                <input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10"
                  placeholder="••••••••"
                  data-testid="login-password-input"
                />
              </div>
            </div>

            {error && (
              <div className="flex items-start gap-2 p-3 bg-bad/10 border border-bad/30 rounded-sm text-bad text-xs" data-testid="login-error">
                <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-11 font-semibold"
              data-testid="login-submit-button"
            >
              {loading ? "Connexion…" : <>Se connecter <ArrowRight size={16} /></>}
            </button>

            <p className="text-center text-xs text-obsidian-500 pt-4">
              Accès réservé au personnel autorisé · En cas de problème, contactez la Direction
            </p>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
